import { createFileRoute, redirect, useRouter } from '@tanstack/react-router'

import { SignInIncomplete } from "@/components/sign-in-incomplete";
import {
  hasAttemptedInteractiveLogin,
  hasExceededLoginBounces,
  isOidcEnabled,
  redirectToInteractiveLogin,
  stashReturnUrl,
} from "@/lib/auth/oidc-bootstrap";
import { getToken, isTwoFactorInProgress } from "@/lib/token/token";
import LoginPage from "@/modules/login/components/login-page";

export const Route = createFileRoute('/login')({
  beforeLoad: (): void | Promise<never> => {
    // If the user has the Two Factor role, go to /two-factor!
    if (isTwoFactorInProgress()) {
      throw redirect({'to': '/two-factor'})
    }

    // If there's a token set, it's reasonable to assume they're logged in.
    if (getToken()) {
      throw redirect({'to': '/'})
    }

    // Keycloak owns login: /login is a shim. Stash the return target, then leave for Keycloak.
    // A prior attempt that ended back here without a session falls through to the retry state
    // instead of redirecting again.
    // The second condition is the per-tab "one attempt outstanding" guard; the third is the
    // loop cap. Both are needed: a successful exchange clears the first, so on a journey where
    // the exchange keeps succeeding and something afterwards fails, the first can never hold.
    if (isOidcEnabled() && !hasAttemptedInteractiveLogin() && !hasExceededLoginBounces()) {
      stashReturnUrl()

      return redirectToInteractiveLogin()
    }
  },

  component: RouteComponent,
})

function RouteComponent(){
  const router = useRouter()
  const navigate = Route.useNavigate()

  if (isOidcEnabled()) {
    return <SignInIncomplete />
  }

  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10 bg-[#022D4F]">
      <LoginPage router={router} navigate={navigate} />
    </div>
  )
}
